import { useCallback } from "react";

import { useUploadErrorDialog } from "@/contexts/UploadErrorDialogContext";
import { isHeicFile } from "@/lib/normalizeImageFile";

/**
 * Single entry point every flow uses to vet a freshly-picked file before
 * upload. Centralizes the unsupported-format UX so it lives in exactly one
 * place instead of being copy-pasted into ~12 flows:
 *
 *   - HEIC/HEIF is NOT supported (we don't convert it — browser conversion
 *     needs WASM blocked by our CSP, and server-side decoding is too slow).
 *     If any picked file is HEIC we open the global UploadErrorDialog guiding
 *     the user to JPG/PNG/WebP and return `null` so the caller bails.
 *   - Everything else passes straight through untouched.
 *
 * Usage:
 *   const { normalizeOrWarn } = useImageUpload();
 *   const file = await normalizeOrWarn(raw);
 *   if (!file) return; // dialog already shown
 */
export function useImageUpload() {
  const { open: openUploadError } = useUploadErrorDialog();

  const normalizeOrWarn = useCallback(
    (file: File): File | null => {
      if (isHeicFile(file)) {
        openUploadError();
        return null;
      }
      return file;
    },
    [openUploadError],
  );

  const normalizeFilesOrWarn = useCallback(
    (files: File[]): File[] | null => {
      if (files.some(isHeicFile)) {
        openUploadError();
        return null;
      }
      return files;
    },
    [openUploadError],
  );

  return { normalizeOrWarn, normalizeFilesOrWarn };
}
